'use client';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/components/auth-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  Activity,
  Ban,
  Eye,
  Mail,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  Trash2,
} from 'lucide-react';

/**
 * Transactional email desk (docs/internal/launch/transactional-email-spec.md §5).
 *
 * Four sections: the health strip, the message log, the suppression list and the
 * template preview. Admin and moderator can read everything here; resend, add and
 * remove are admin only.
 *
 * Privacy stance for moderators: the log shows the recipient address, the
 * template and what we sent. It shows no unrelated account data and there is no
 * view-as-user path.
 */

interface HealthWindow {
  counts: Record<string, number>;
  sent: number;
  bounced: number;
  complained: number;
  rate: number;
  queued: number;
  deferred: number;
}

interface Health {
  enabled: boolean;
  providerConfigured: boolean;
  day: HealthWindow;
  week: HealthWindow;
}

interface MessageRow {
  id: string;
  userId: string | null;
  toEmail: string;
  template: string;
  category: string;
  subject: string;
  status: string;
  idempotencyKey: string;
  providerMessageId: string | null;
  error: string | null;
  attempts: number;
  data: unknown;
  createdAt: string;
  sentAt: string | null;
  updatedAt: string;
}

interface SuppressionRow {
  email: string;
  reason: string;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface TemplateInfo {
  name: string;
  category: string;
  sample: unknown;
}

interface Rendered {
  subject: string;
  html: string;
  text: string;
  category: string;
}

// 'Opted out' and 'Expired' are separate from 'Suppressed' on purpose: a
// suppression is a deliverability problem, an opt-out is consent working, and an
// expiry is the queue's own age cutoff. One filter for all three hid the
// difference.
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'suppressed', label: 'Suppressed' },
  { value: 'opted_out', label: 'Opted out' },
  { value: 'expired', label: 'Expired' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
  { value: 'failed', label: 'Failed' },
];

/** Statuses are stored as identifiers; the table shows the readable form. */
function statusLabel(status: string): string {
  return STATUS_FILTERS.find((s) => s.value === status)?.label ?? status;
}

const REASONS = ['bounced', 'complained', 'unsubscribed', 'manual'];

const inputCls =
  'w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary';
const btnCls =
  'px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50 disabled:pointer-events-none';
const primaryBtnCls =
  'px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:pointer-events-none';
const selectCls = 'text-xs rounded-md border border-border bg-background px-2 py-1.5';

function shortDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function percent(rate: number): string {
  return `${(rate * 100).toFixed(rate > 0 && rate < 0.001 ? 3 : 1)}%`;
}

function SectionCard({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2 min-w-[104px]">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-base font-semibold', tone === 'warn' && 'text-destructive')}>
        {value}
      </div>
    </div>
  );
}

/**
 * The templates carry their dark palette inside a prefers-color-scheme block, and
 * an iframe follows the browser, not this toggle. For the preview only, promote
 * that block so the dark rendering can be checked on a light machine.
 */
function forceScheme(html: string, scheme: 'light' | 'dark'): string {
  if (scheme === 'light') return html;
  return html.replace(/@media \(prefers-color-scheme: dark\)/g, '@media all');
}

export function EmailTab() {
  const { user, loading } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isStaff = isAdmin || user?.role === 'moderator';

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const flash = useCallback((msg: string, isError = false) => {
    setError(isError ? msg : null);
    setNotice(isError ? null : msg);
  }, []);

  // Health strip
  const [health, setHealth] = useState<Health | null>(null);

  // Message log
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filters, setFilters] = useState({ status: '', template: '', recipient: '', since: '' });
  const [busyId, setBusyId] = useState<string | null>(null);

  // Suppressions
  const [suppressions, setSuppressions] = useState<SuppressionRow[]>([]);
  const [suppressionQuery, setSuppressionQuery] = useState('');
  const [addForm, setAddForm] = useState({ email: '', reason: 'manual', note: '' });
  const [addBusy, setAddBusy] = useState(false);

  // Template preview
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [previewScheme, setPreviewScheme] = useState<'light' | 'dark'>('light');

  const loadHealth = useCallback(async () => {
    const res = await fetch('/api/admin/email/health');
    if (res.ok) setHealth(await res.json());
  }, []);

  const loadMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.status) qs.set('status', filters.status);
      if (filters.template) qs.set('template', filters.template);
      if (filters.recipient.trim()) qs.set('recipient', filters.recipient.trim());
      if (filters.since) qs.set('since', new Date(filters.since).toISOString());
      const res = await fetch(`/api/admin/email/messages?${qs.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages ?? []);
      }
    } finally {
      setMessagesLoading(false);
    }
  }, [filters]);

  const loadSuppressions = useCallback(async () => {
    const qs = suppressionQuery.trim() ? `?q=${encodeURIComponent(suppressionQuery.trim())}` : '';
    const res = await fetch(`/api/admin/email/suppressions${qs}`);
    if (res.ok) {
      const data = await res.json();
      setSuppressions(data.suppressions ?? []);
    }
  }, [suppressionQuery]);

  const loadTemplates = useCallback(async () => {
    const res = await fetch('/api/admin/email/preview');
    if (res.ok) {
      const data = await res.json();
      setTemplates(data.templates ?? []);
      if (!selected && data.templates?.length) setSelected(data.templates[0].name);
    }
  }, [selected]);

  const loadPreview = useCallback(async (name: string) => {
    if (!name) return;
    const res = await fetch(`/api/admin/email/preview?template=${encodeURIComponent(name)}`);
    if (res.ok) setRendered(await res.json());
    else setRendered(null);
  }, []);

  useEffect(() => {
    if (!isStaff) return;
    void loadHealth();
    void loadTemplates();
    // Templates and health load once; the log and the list have their own triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  useEffect(() => {
    if (isStaff) void loadMessages();
  }, [isStaff, loadMessages]);

  useEffect(() => {
    if (isStaff) void loadSuppressions();
  }, [isStaff, loadSuppressions]);

  useEffect(() => {
    if (isStaff && selected) void loadPreview(selected);
  }, [isStaff, selected, loadPreview]);

  const resend = useCallback(
    async (row: MessageRow) => {
      if (!window.confirm(`Resend "${row.subject}" to ${row.toEmail}? This queues a new message with a new key.`)) {
        return;
      }
      setBusyId(row.id);
      try {
        const res = await fetch(`/api/admin/email/messages/${row.id}/resend`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) flash(data.error ?? 'Resend failed.', true);
        else flash(`Resend queued (${data.outcome}).`);
        await loadMessages();
      } finally {
        setBusyId(null);
      }
    },
    [flash, loadMessages],
  );

  const addSuppression = useCallback(async () => {
    if (!addForm.email.trim()) {
      flash('Enter an address to suppress.', true);
      return;
    }
    setAddBusy(true);
    try {
      const res = await fetch('/api/admin/email/suppressions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: addForm.email.trim(),
          reason: addForm.reason,
          note: addForm.note.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) flash(data.error ?? 'Could not add.', true);
      else {
        flash('Suppression added.');
        setAddForm({ email: '', reason: 'manual', note: '' });
        await loadSuppressions();
      }
    } finally {
      setAddBusy(false);
    }
  }, [addForm, flash, loadSuppressions]);

  const removeSuppression = useCallback(
    async (row: SuppressionRow) => {
      if (!window.confirm(`Remove ${row.email} from the suppression list? Mail to this address will resume.`)) {
        return;
      }
      const res = await fetch(`/api/admin/email/suppressions?email=${encodeURIComponent(row.email)}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) flash(data.error ?? 'Could not remove.', true);
      else flash('Suppression removed.');
      await loadSuppressions();
    },
    [flash, loadSuppressions],
  );

  const previewSrc = useMemo(
    () => (rendered ? forceScheme(rendered.html, previewScheme) : ''),
    [rendered, previewScheme],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-2">
        <ShieldAlert className="h-6 w-6 mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          This page is for administrators and moderators.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Transactional email</h1>
        <button
          className={btnCls}
          onClick={() => {
            void loadHealth();
            void loadMessages();
            void loadSuppressions();
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </span>
        </button>
      </div>

      {(notice || error) && (
        <div
          className={cn(
            'text-sm rounded-lg border px-3 py-2',
            error ? 'border-destructive/40 text-destructive' : 'border-border text-muted-foreground',
          )}
        >
          {error ?? notice}
        </div>
      )}

      <SectionCard
        icon={<Activity className="h-4 w-4 text-muted-foreground" />}
        title="Health"
        subtitle="Bounce rate is the number that predicts deliverability problems, so it leads."
      >
        {!health ? (
          <div className="flex justify-center py-6">
            <Spinner size="xs" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs">
              <span
                className={cn(
                  'px-2.5 py-1 rounded-full border',
                  health.enabled ? 'border-border' : 'border-destructive/40 text-destructive',
                )}
              >
                {health.enabled ? 'Sending is on' : 'Sending is off, the queue is holding'}
              </span>
              <span
                className={cn(
                  'px-2.5 py-1 rounded-full border',
                  health.providerConfigured
                    ? 'border-border'
                    : 'border-destructive/40 text-destructive',
                )}
              >
                {health.providerConfigured ? 'Provider configured' : 'No provider credentials'}
              </span>
            </div>

            {(['day', 'week'] as const).map((key) => {
              const w = health[key];
              return (
                <div key={key} className="space-y-1.5">
                  <div className="text-xs text-muted-foreground">
                    {key === 'day' ? 'Last 24 hours' : 'Last 7 days'}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Stat
                      label="Bounce rate"
                      value={percent(w.rate)}
                      tone={w.rate >= 0.02 ? 'warn' : undefined}
                    />
                    <Stat label="Accepted" value={String(w.sent)} />
                    <Stat label="Delivered" value={String(w.counts.delivered ?? 0)} />
                    <Stat label="Bounced" value={String(w.bounced)} />
                    <Stat label="Complained" value={String(w.complained)} />
                    <Stat label="Queued" value={String(w.queued)} />
                    <Stat label="Deferred" value={String(w.deferred)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<Mail className="h-4 w-4 text-muted-foreground" />}
        title="Message log"
        subtitle="Every send attempt, including the ones that were suppressed or held back."
      >
        <div className="grid gap-2 sm:grid-cols-4">
          <select
            className={selectCls}
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className={selectCls}
            value={filters.template}
            onChange={(e) => setFilters((f) => ({ ...f, template: e.target.value }))}
          >
            <option value="">All templates</option>
            {templates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
          <input
            className={inputCls}
            placeholder="Recipient contains"
            value={filters.recipient}
            onChange={(e) => setFilters((f) => ({ ...f, recipient: e.target.value }))}
          />
          <input
            className={inputCls}
            type="date"
            value={filters.since}
            onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
          />
        </div>

        {messagesLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="xs" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No messages.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">Recipient</th>
                  <th className="py-2 pr-3 font-medium">Template</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Created</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {messages.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td className="py-2 pr-3">
                        <button
                          className="text-left hover:underline"
                          onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                        >
                          {row.toEmail}
                        </button>
                        <div className="text-xs text-muted-foreground max-w-[280px] truncate">
                          {row.subject}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-xs">{row.template}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={cn(
                            'text-xs',
                            (row.status === 'bounced' ||
                              row.status === 'complained' ||
                              row.status === 'failed') &&
                              'text-destructive',
                          )}
                        >
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                        {shortDateTime(row.createdAt)}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1.5">
                          <button
                            className={btnCls}
                            onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                          >
                            <span className="inline-flex items-center gap-1">
                              <Eye className="h-3.5 w-3.5" />
                              {expanded === row.id ? 'Hide' : 'Details'}
                            </span>
                          </button>
                          {isAdmin && (
                            <button
                              className={btnCls}
                              disabled={busyId === row.id}
                              onClick={() => void resend(row)}
                            >
                              <span className="inline-flex items-center gap-1">
                                <Send className="h-3.5 w-3.5" /> Resend
                              </span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === row.id && (
                      <tr>
                        <td colSpan={5} className="py-3">
                          <div className="rounded-lg border border-border p-3 space-y-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Subject: </span>
                              {row.subject}
                            </div>
                            <div>
                              <span className="text-muted-foreground">Category: </span>
                              {row.category}
                              <span className="text-muted-foreground"> · Attempts: </span>
                              {row.attempts}
                              <span className="text-muted-foreground"> · Sent: </span>
                              {shortDateTime(row.sentAt) || 'not yet'}
                            </div>
                            <div className="break-all">
                              <span className="text-muted-foreground">Idempotency key: </span>
                              {row.idempotencyKey}
                            </div>
                            <div className="break-all">
                              <span className="text-muted-foreground">Provider id: </span>
                              {row.providerMessageId ?? 'none'}
                            </div>
                            {row.error && (
                              <div className="text-destructive break-all">
                                <span className="text-muted-foreground">Reason: </span>
                                {row.error}
                              </div>
                            )}
                            <div>
                              <div className="text-muted-foreground mb-1">Payload</div>
                              <pre className="overflow-x-auto rounded-md border border-border p-2">
                                {JSON.stringify(row.data, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<Ban className="h-4 w-4 text-muted-foreground" />}
        title="Suppression list"
        subtitle="Addresses we stopped mailing. A complaint should stay on the list; a bounce can be cleared once the mailbox is fixed."
      >
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className={cn(inputCls, 'pl-8')}
              placeholder="Search addresses"
              value={suppressionQuery}
              onChange={(e) => setSuppressionQuery(e.target.value)}
            />
          </div>
        </div>

        {isAdmin && (
          <div className="grid gap-2 sm:grid-cols-4">
            <input
              className={cn(inputCls, 'sm:col-span-2')}
              placeholder="Address to suppress"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
            />
            <select
              className={selectCls}
              value={addForm.reason}
              onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))}
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button className={primaryBtnCls} disabled={addBusy} onClick={() => void addSuppression()}>
              Add
            </button>
            <input
              className={cn(inputCls, 'sm:col-span-4')}
              placeholder="Note (optional)"
              value={addForm.note}
              onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))}
            />
          </div>
        )}

        {suppressions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">Nothing suppressed.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">Address</th>
                  <th className="py-2 pr-3 font-medium">Reason</th>
                  <th className="py-2 pr-3 font-medium">Note</th>
                  <th className="py-2 pr-3 font-medium">Added</th>
                  {isAdmin && <th className="py-2 font-medium"> </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {suppressions.map((row) => (
                  <tr key={row.email}>
                    <td className="py-2 pr-3 break-all">{row.email}</td>
                    <td className="py-2 pr-3 text-xs">{row.reason}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[240px] truncate" title={row.note ?? ''}>
                      {row.note}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                      {shortDateTime(row.createdAt)}
                    </td>
                    {isAdmin && (
                      <td className="py-2">
                        <button className={btnCls} onClick={() => void removeSuppression(row)}>
                          <span className="inline-flex items-center gap-1">
                            <Trash2 className="h-3.5 w-3.5" /> Remove
                          </span>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<Eye className="h-4 w-4 text-muted-foreground" />}
        title="Template preview"
        subtitle="Every template against sample data, in HTML and plaintext, light and dark."
      >
        <div className="flex flex-wrap gap-2">
          <select className={selectCls} value={selected} onChange={(e) => setSelected(e.target.value)}>
            {templates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({t.category})
              </option>
            ))}
          </select>
          <div className="flex gap-1.5">
            {(['html', 'text'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setPreviewMode(m)}
                className={cn(
                  'px-2.5 py-1 text-xs rounded-full border transition-colors',
                  previewMode === m
                    ? 'border-primary text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent',
                )}
              >
                {m === 'html' ? 'HTML' : 'Plaintext'}
              </button>
            ))}
          </div>
          {previewMode === 'html' && (
            <div className="flex gap-1.5">
              {(['light', 'dark'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setPreviewScheme(s)}
                  className={cn(
                    'px-2.5 py-1 text-xs rounded-full border transition-colors',
                    previewScheme === s
                      ? 'border-primary text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent',
                  )}
                >
                  {s === 'light' ? 'Light' : 'Dark'}
                </button>
              ))}
            </div>
          )}
        </div>

        {!rendered ? (
          <p className="text-sm text-muted-foreground py-4">Pick a template.</p>
        ) : (
          <div className="space-y-2">
            <div className="text-xs">
              <span className="text-muted-foreground">Subject: </span>
              {rendered.subject}
            </div>
            {previewMode === 'html' ? (
              <iframe
                title="Email preview"
                className="w-full h-[560px] rounded-lg border border-border bg-white"
                sandbox=""
                srcDoc={previewSrc}
              />
            ) : (
              <pre className="w-full h-[560px] overflow-auto rounded-lg border border-border p-3 text-xs whitespace-pre-wrap">
                {rendered.text}
              </pre>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
